export interface ProgressState {
  progress: number;
  isNear: boolean;
  isOvertime: boolean;
  isUnder: boolean;
}

export interface ProgressThresholds {
  near?: number;
}

/**
 * Calculate progress state based on value, max, and thresholds
 * @param value Current value
 * @param max Maximum value
 * @param thresholds Thresholds for different states
 * @returns ProgressState object with calculated values
 */
export const calculateProgressState = (
  value: number,
  max: number,
  thresholds: ProgressThresholds = { near: 0.9 }
): ProgressState => {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max === 0) {
    return {
      progress: 0,
      isNear: false,
      isOvertime: false,
      isUnder: true,
    };
  }

  const progress = value / max;
  const isEqual = value === max;
  const isNear = !isEqual && Math.abs(progress - 1) <= 0.2;
  const isOvertime = value > max;
  const isUnder = progress < (thresholds.near || 0.9);

  return {
    progress,
    isNear,
    isOvertime,
    isUnder,
  };
};

/**
 * Determine color based on progress state
 * @param progressState Calculated progress state
 * @param colorScheme Color scheme object
 * @returns Selected color string
 */
export const getProgressColor = (
  progressState: ProgressState,
  colorScheme: {
    under?: string;
    near?: string;
    overtime?: string;
    default?: string;
  }
): string => {
  const { isOvertime, isNear, isUnder } = progressState;

  if (isOvertime) {
    return colorScheme.overtime || colorScheme.default || '#000000';
  } else if (isNear) {
    return colorScheme.near || colorScheme.default || '#000000';
  } else if (isUnder) {
    return colorScheme.under || colorScheme.default || '#000000';
  } else {
    return colorScheme.default || '#000000';
  }
};
